import React, {useMemo, useCallback, useEffect} from 'react'
import styled from 'styled-components'
import {CsvRow} from './types'
import {isValidEmail} from './utils'
import {useI18n} from 'i18n/I18nContext'

interface Props {
  rows: CsvRow[]
  selectedEmailColumn: string
  onBack: () => void
  onRequestSend: (emails: string[], fileName: string) => void
  file: File | null
  setTotalUniqueValidEmails: (count: number) => void
}

const Step3PreviewSend: React.FC<Props> = ({
  rows,
  selectedEmailColumn,
  onBack,
  onRequestSend,
  file,
  setTotalUniqueValidEmails,
}) => {
  const {t} = useI18n()
  const allEmails = useMemo(
    () =>
      rows.map((r) => (r[selectedEmailColumn] || '').trim()).filter(Boolean),
    [rows, selectedEmailColumn],
  )
  const validEmails = useMemo(() => allEmails.filter(isValidEmail), [allEmails])
  const uniqueValidEmails = useMemo(
    () => [...new Set(validEmails.map((e) => e.toLowerCase()))],
    [validEmails],
  )

  useEffect(() => {
    setTotalUniqueValidEmails(uniqueValidEmails.length)
  }, [setTotalUniqueValidEmails, uniqueValidEmails])
  const invalidEmails = useMemo(
    () => allEmails.filter((e) => !isValidEmail(e)).slice(0, 5),
    [allEmails],
  )
  const duplicateCount = validEmails.length - uniqueValidEmails.length
  const previewEmails = allEmails.slice(0, 20)

  const handleOpenModal = useCallback(() => {
    if (!uniqueValidEmails.length) return
    onRequestSend(uniqueValidEmails, file?.name ?? 'no_name.csv')
  }, [file, onRequestSend, uniqueValidEmails])

  return (
    <Wrapper>
      <Header>
        <button className="disableBtnStyle" onClick={onBack}>
          {t('orgCsv.back')}
        </button>
        <button
          className="input__btn"
          onClick={handleOpenModal}
          disabled={!uniqueValidEmails.length}
        >
          {t('orgCsv.nextSend')}
        </button>
      </Header>

      <Title>
        {t('orgCsv.step3Heading')} <br /> {t('orgCsv.step3Sub')}
      </Title>

      <InfoBar>
        <StatChip>
          {t('orgCsv.statTotalRows')}
          <div>{rows.length}</div>
        </StatChip>
        <StatChip>
          {t('orgCsv.statValid')}
          <div>{validEmails.length}</div>
        </StatChip>
        <StatChip>
          {t('orgCsv.statDup')}
          <div>{duplicateCount}</div>
        </StatChip>
        <StatChip>
          {t('orgCsv.statUnique')}
          <div>{uniqueValidEmails.length}</div>
        </StatChip>
      </InfoBar>

      {invalidEmails.length > 0 && (
        <Note>
          {t('orgCsv.invalidSample')} {invalidEmails.join(', ')}
        </Note>
      )}

      <PreviewInfo>{t('orgCsv.preview20')}</PreviewInfo>
      <EmailList>
        {previewEmails.map((e, i) => (
          <EmailCard key={i}>{e}</EmailCard>
        ))}
      </EmailList>
    </Wrapper>
  )
}

export default Step3PreviewSend

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h3`
  margin: 0;
  text-align: center;
  margin-top: 4px;
`

const InfoBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`
const StatChip = styled.div`
  background: #ebebeb;
  border-radius: 5px;
  padding: 10px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
`
const Note = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #5f6368;
`
const PreviewInfo = styled.div`
  font-size: 14px;
  color: #5f6368;
`
const EmailList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`
const EmailCard = styled.div`
  background: #2e353a;
  padding: 6px 12px;
  border-radius: 5px;
  color: #ffffff;
`
